import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiTrendingUp,
  FiShield,
  FiZap,
  FiAward,
  FiUsers,
  FiBarChart2,
  FiArrowRight,
  FiCheck,
  FiStar,
  FiClock,
  FiDollarSign,
  FiSmartphone,
  FiGlobe,
  FiMessageCircle,
  FiThumbsUp,
  FiTarget,
  FiActivity,
  FiGift,
  FiX,
} from 'react-icons/fi';

const Home: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [selectedNews, setSelectedNews] = useState<{
    title: string;
    description: string;
    date: string;
    tag: string;
    color: string;
    fullContent: string;
  } | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (selectedNews) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedNews]);

  const offers = [
    {
      id: 'circusbet',
      name: 'CircusBet',
      bonus: '100€ + 50 tours gratuits',
      rating: 4.8,
      image: '/images/offers/circusbet.webp',
      link: '/offres/circusbet',
      url: 'https://www.circusbet.fr/',
      features: ['Bonus de bienvenue généreux', 'Cotes compétitives', 'Support 24/7'],
    },
    {
      id: 'genybet',
      name: 'GenyBet',
      bonus: '150€ de bonus',
      rating: 4.7,
      image: '/images/offers/genybet.webp',
      link: '/offres/genybet',
      url: 'https://www.genybet.fr/',
      features: ['Interface moderne', 'Paiements rapides', 'Application mobile'],
    },
    {
    id: 'netbet',
      name: 'NetBet',
      bonus: '200€ + 100 tours',
      rating: 4.9,
      image: '/images/offers/netbet.webp',
      link: '/offres/netbet',
      url: 'https://www.netbet.fr/',
      features: ['Meilleures cotes', 'Cashback hebdomadaire', 'VIP exclusif'],
    },
  ];

  const stats = [
    { icon: FiUsers, value: '250K+', label: 'Joueurs actifs' },
    { icon: FiAward, value: '15+', label: 'Bookmakers vérifiés' },
    { icon: FiTrendingUp, value: '98%', label: 'Taux de satisfaction' },
    { icon: FiZap, value: '24/7', label: 'Support disponible' },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: "Let's Try Bet",
            url: 'https://letstrybet.com',
            description:
              'Guide expert des meilleures offres de paris sportifs en France. Comparez les bookmakers et trouvez les meilleures cotes.',
            publisher: {
              '@type': 'Organization',
              name: "Let's Try Bet",
            },
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://letstrybet.com/recherche?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: "Let's Try Bet",
            url: 'https://letstrybet.com',
            logo: 'https://letstrybet.com/images/logo.webp',
            description:
              'Comparateur de paris sportifs en France. Trouvez les meilleures offres et bonus.',
            sameAs: [
              'https://facebook.com/letstrybet',
              'https://twitter.com/letstrybet',
            ],
          }),
        }}
      />

      {/* Hero Section with 6 Anchors */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center overflow-hidden pt-20 bg-dark-bg"
      >
        <div className="container mx-auto px-4 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="text-left">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 animate-fade-in">
                Les Meilleures Offres de
                <span className="text-accent-primary block">Paris Sportifs en France</span>
              </h1>
              <p className="text-lg md:text-xl text-dark-muted mb-8 animate-slide-up">
                Comparez les meilleurs bookmakers, profitez de bonus exclusifs et maximisez vos gains
                avec notre guide expert.
              </p>
              
              {/* 6 Anchor Links */}
              <div className="flex flex-wrap gap-4 mb-8 animate-slide-up">
                <a
                  href="#offres"
                  className="bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-105"
                >
                  Voir les offres
                </a>
                <a
                  href="#comparaison"
                  className="bg-dark-surface hover:bg-dark-border text-white font-semibold py-3 px-6 rounded-lg transition-all border border-dark-border transform hover:scale-105"
                >
                  Comparer
                </a>
                <a
                  href="#avantages"
                  className="bg-dark-surface hover:bg-dark-border text-white font-semibold py-3 px-6 rounded-lg transition-all border border-dark-border transform hover:scale-105"
                >
                  Avantages
                </a>
                <a
                  href="#statistiques"
                  className="bg-dark-surface hover:bg-dark-border text-white font-semibold py-3 px-6 rounded-lg transition-all border border-dark-border transform hover:scale-105"
                >
                  Statistiques
                </a>
                <a
                  href="#guide"
                  className="bg-dark-surface hover:bg-dark-border text-white font-semibold py-3 px-6 rounded-lg transition-all border border-dark-border transform hover:scale-105"
                >
                  Guide
                </a>
                <a
                  href="#faq"
                  className="bg-dark-surface hover:bg-dark-border text-white font-semibold py-3 px-6 rounded-lg transition-all border border-dark-border transform hover:scale-105"
                >
                  FAQ
                </a>
              </div>
            </div>

            {/* Right Column - Image Card */}
            <div className="relative animate-slide-up">
              <div className="bg-dark-card border border-dark-border rounded-lg p-6 shadow-2xl hover:border-accent-primary transition-all transform hover:scale-105">
                <img
                  src="/images/heroroght.webp"
                  alt="Paris Sportifs"
                  className="w-full h-auto rounded-lg"
                  loading="eager"
                  width="600"
                  height="400"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent-primary/20 rounded-full blur-2xl -z-10" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-accent-secondary/20 rounded-full blur-3xl -z-10" />
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <FiArrowRight className="text-white rotate-90" size={24} />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="statistiques" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Pourquoi Nous Choisir ?
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-lg p-6 text-center hover:border-accent-primary transition-all transform hover:scale-105"
              >
                <stat.icon className="mx-auto mb-4 text-accent-primary" size={32} />
                <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-dark-muted">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offers Section */}
      <section id="offres" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Meilleures Offres du Moment
          </h2>
          <p className="text-center text-dark-muted mb-12 max-w-2xl mx-auto">
            Découvrez les bookmakers les plus avantageux avec des bonus exclusifs et des
            conditions de retrait optimisées.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {offers.map((offer, index) => (
              <div
                key={offer.id}
                className="bg-dark-card border border-dark-border rounded-lg overflow-hidden hover:border-accent-primary transition-all transform hover:scale-105 group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-48 bg-dark-surface">
                  <img
                    src={offer.image}
                    alt={offer.name}
                    className="w-full h-full object-contain p-4"
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4 bg-accent-primary text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    {offer.rating} <FiStar className="fill-current" size={14} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{offer.name}</h3>
                  <div className="text-accent-primary font-semibold mb-4">
                    {offer.bonus}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {offer.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-dark-muted text-sm">
                        <FiCheck className="text-accent-success mr-2" size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-3">
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-3 px-6 rounded-lg text-center transition-colors group-hover:shadow-lg inline-flex items-center justify-center"
                    >
                      Jouer maintenant <FiArrowRight className="inline ml-2" />
                    </a>
                    <Link
                      to={offer.link}
                      className="px-4 py-3 bg-dark-surface hover:bg-dark-border text-white font-semibold rounded-lg transition-colors border border-dark-border inline-flex items-center justify-center"
                      title="Voir l'avis détaillé"
                    >
                      Avis
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section id="avantages" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Avantages Exclusifs
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FiShield,
                title: 'Sécurité Garantie',
                description:
                  'Tous nos partenaires sont licenciés et régulés par l\'ANJ pour une expérience 100% sécurisée.',
              },
              {
                icon: FiZap,
                title: 'Paiements Rapides',
                description:
                  'Retraits traités en moins de 24h avec les méthodes de paiement les plus fiables du marché.',
              },
              {
                icon: FiBarChart2,
                title: 'Cotes Optimisées',
                description:
                  'Accédez aux meilleures cotes du marché grâce à notre comparaison en temps réel.',
              },
            ].map((advantage, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-lg p-8 hover:border-accent-primary transition-all"
              >
                <advantage.icon className="text-accent-primary mb-4" size={40} />
                <h3 className="text-xl font-bold text-white mb-3">{advantage.title}</h3>
                <p className="text-dark-muted">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison CTA */}
      <section id="comparaison" className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-accent-primary to-accent-secondary rounded-lg p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Comparez Tous les Bookmakers
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Utilisez notre outil de comparaison pour trouver le bookmaker qui correspond
              le mieux à vos besoins.
            </p>
            <Link
              to="/comparaison"
              className="inline-block bg-white text-accent-primary font-semibold py-3 px-8 rounded-lg hover:bg-white/90 transition-colors"
            >
              Comparer maintenant
            </Link>
          </div>
        </div>
      </section>

      {/* Guide Section */}
      <section id="guide" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Guide du Débutant
          </h2>
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                step: '01',
                title: 'Choisissez votre bookmaker',
                description:
                  'Sélectionnez un bookmaker licencié en France avec des bonus attractifs et des conditions claires.',
              },
              {
                step: '02',
                title: 'Créez votre compte',
                description:
                  'Inscrivez-vous en quelques minutes avec une vérification d\'identité rapide et sécurisée.',
              },
              {
                step: '03',
                title: 'Réclamez votre bonus',
                description:
                  'Activez votre bonus de bienvenue et commencez à parier avec des fonds supplémentaires.',
              },
              {
                step: '04',
                title: 'Placez vos paris',
                description:
                  'Explorez les marchés disponibles et placez vos paris sur vos sports favoris.',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-start bg-dark-card border border-dark-border rounded-lg p-6 hover:border-accent-primary transition-all"
              >
                <div className="text-4xl font-bold text-accent-primary mr-6">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-dark-muted">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Sports Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Sports les Plus Populaires
          </h2>
          <p className="text-center text-dark-muted mb-12 max-w-2xl mx-auto">
            Pariez sur vos sports favoris avec les meilleures cotes du marché
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              { name: 'Football', icon: FiActivity, color: 'from-green-500 to-emerald-600' },
              { name: 'Tennis', icon: FiZap, color: 'from-yellow-500 to-orange-500' },
              { name: 'Basketball', icon: FiTrendingUp, color: 'from-orange-500 to-red-500' },
              { name: 'Rugby', icon: FiTarget, color: 'from-blue-500 to-indigo-600' },
              { name: 'Cyclisme', icon: FiActivity, color: 'from-cyan-500 to-blue-500' },
              { name: 'Handball', icon: FiAward, color: 'from-purple-500 to-pink-500' },
            ].map((sport, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-lg p-6 text-center hover:border-accent-primary transition-all transform hover:scale-105 cursor-pointer group"
              >
                <div className={`mb-3 group-hover:scale-110 transition-transform flex justify-center`}>
                  <sport.icon className="text-accent-primary" size={40} />
                </div>
                <div className="text-white font-semibold">{sport.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Ce Que Disent Nos Utilisateurs
          </h2>
          <p className="text-center text-dark-muted mb-12 max-w-2xl mx-auto">
            Des milliers de joueurs nous font confiance pour trouver les meilleures offres
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Jean Dupont',
                rating: 5,
                comment:
                  'Grâce à Let\'s Try Bet, j\'ai trouvé le bookmaker parfait avec des cotes excellentes et des retraits rapides. Je recommande vivement !',
                date: 'Il y a 2 semaines',
              },
              {
                name: 'Marie Martin',
                rating: 5,
                comment:
                  'Comparaison très utile, interface claire et informations précises. J\'ai économisé beaucoup de temps dans ma recherche.',
                date: 'Il y a 1 mois',
              },
              {
                name: 'Pierre Bernard',
                rating: 5,
                comment:
                  'Excellent service, les bonus sont vraiment avantageux et les explications sont très claires. Parfait pour débuter !',
                date: 'Il y a 3 semaines',
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-lg p-6 hover:border-accent-primary transition-all"
              >
                <div className="flex items-center mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <FiStar
                      key={i}
                      className="text-yellow-400 fill-current mr-1"
                      size={20}
                    />
                  ))}
                </div>
                <p className="text-dark-muted mb-4 leading-relaxed">
                  "{testimonial.comment}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold">{testimonial.name}</div>
                    <div className="text-dark-muted text-sm">{testimonial.date}</div>
                  </div>
                  <FiThumbsUp className="text-accent-primary" size={24} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Comment Ça Marche ?
          </h2>
          <p className="text-center text-dark-muted mb-12 max-w-2xl mx-auto">
            En 4 étapes simples, trouvez le bookmaker idéal pour vous
          </p>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 relative">
              {/* Connection lines */}
              <div className="hidden md:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-accent-primary via-accent-secondary to-accent-primary opacity-30" />
              
              {[
                {
                  icon: FiTarget,
                  title: 'Explorez',
                  description: 'Parcourez notre sélection de bookmakers vérifiés',
                },
                {
                  icon: FiBarChart2,
                  title: 'Comparez',
                  description: 'Utilisez notre outil pour comparer les offres',
                },
                {
                  icon: FiCheck,
                  title: 'Choisissez',
                  description: 'Sélectionnez celui qui correspond à vos besoins',
                },
                {
                  icon: FiGift,
                  title: 'Profitez',
                  description: 'Réclamez votre bonus et commencez à parier',
                },
              ].map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-dark-card border border-dark-border rounded-lg p-6 text-center hover:border-accent-primary transition-all">
                    <div className="bg-accent-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <step.icon className="text-accent-primary" size={32} />
                    </div>
                    <div className="text-white font-bold text-lg mb-2">{step.title}</div>
                    <p className="text-dark-muted text-sm">{step.description}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-12 -right-4 w-8 h-0.5 bg-accent-primary" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Deep Dive Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Fonctionnalités Avancées
          </h2>
          <p className="text-center text-dark-muted mb-12 max-w-2xl mx-auto">
            Découvrez toutes les fonctionnalités qui font la différence
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: FiSmartphone,
                title: 'Applications Mobiles',
                description:
                  'Parier en déplacement avec des applications iOS et Android optimisées',
              },
              {
                icon: FiClock,
                title: 'Pari en Direct',
                description:
                  'Suivez les événements en temps réel et ajustez vos paris pendant le match',
              },
              {
                icon: FiDollarSign,
                title: 'Cash-Out',
                description:
                  'Clôturez vos paris avant la fin pour sécuriser vos gains ou limiter les pertes',
              },
              {
                icon: FiActivity,
                title: 'Streaming Live',
                description:
                  'Regardez les matchs en direct directement depuis la plateforme',
              },
              {
                icon: FiGlobe,
                title: 'Multi-Langues',
                description:
                  'Interface disponible en français et plusieurs autres langues',
              },
              {
                icon: FiMessageCircle,
                title: 'Support 24/7',
                description:
                  'Assistance client disponible à tout moment par chat, email ou téléphone',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-lg p-6 hover:border-accent-primary transition-all group"
              >
                <feature.icon className="text-accent-primary mb-4 group-hover:scale-110 transition-transform" size={32} />
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-dark-muted">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Pourquoi Nous Faire Confiance ?
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: FiShield,
                title: '100% Sécurisé',
                description: 'Tous les bookmakers sont licenciés ANJ',
                stat: '15+',
                statLabel: 'Bookmakers vérifiés',
              },
              {
                icon: FiZap,
                title: 'Mises à Jour',
                description: 'Informations actualisées quotidiennement',
                stat: '24/7',
                statLabel: 'Surveillance',
              },
              {
                icon: FiUsers,
                title: 'Communauté',
                description: 'Rejoignez des milliers de parieurs satisfaits',
                stat: '250K+',
                statLabel: 'Utilisateurs',
              },
              {
                icon: FiAward,
                title: 'Expertise',
                description: 'Équipe d\'experts en paris sportifs',
                stat: '10+',
                statLabel: 'Années d\'expérience',
              },
            ].map((trust, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-lg p-8 text-center hover:border-accent-primary transition-all"
              >
                <trust.icon className="text-accent-primary mx-auto mb-4" size={40} />
                <h3 className="text-xl font-bold text-white mb-2">{trust.title}</h3>
                <p className="text-dark-muted text-sm mb-4">{trust.description}</p>
                <div className="text-3xl font-bold text-accent-primary mb-1">
                  {trust.stat}
                </div>
                <div className="text-dark-muted text-sm">{trust.statLabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News/Promotions Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Actualités & Promotions
          </h2>
          <p className="text-center text-dark-muted mb-12 max-w-2xl mx-auto">
            Restez informé des dernières offres et promotions exclusives
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Nouveau Bonus de Bienvenue NetBet',
                description:
                  'Profitez d\'un bonus exceptionnel de 200€ + 100 tours gratuits pour les nouveaux joueurs.',
                date: '15 Déc 2025',
                tag: 'Promotion',
                color: 'from-accent-primary to-accent-secondary',
                fullContent:
                  'NetBet lance une promotion exceptionnelle pour tous les nouveaux joueurs ! Réclamez dès maintenant un bonus de bienvenue de 200€ plus 100 tours gratuits sur les machines à sous sélectionnées. Cette offre limitée est valable uniquement pour les nouveaux comptes créés avant le 31 décembre 2025. Les conditions de mise sont de 3x le montant du bonus, et vous avez 30 jours pour valider votre bonus. N\'attendez plus, profitez de cette opportunité unique pour maximiser vos chances de gagner !',
              },
              {
                title: 'Guide Complet : Comment Choisir un Bookmaker',
                description:
                  'Découvrez tous les critères à prendre en compte pour faire le bon choix.',
                date: '12 Déc 2025',
                tag: 'Guide',
                color: 'from-blue-500 to-cyan-500',
                fullContent:
                  'Choisir le bon bookmaker est essentiel pour une expérience de paris réussie. Dans ce guide complet, nous vous expliquons tous les critères importants : la licence ANJ, les cotes proposées, la rapidité des retraits, la qualité du service client, les bonus et promotions, l\'interface utilisateur, et bien plus encore. Nous vous aidons à comprendre comment évaluer chaque aspect et faire le choix qui correspond le mieux à vos besoins et à votre style de jeu. Que vous soyez débutant ou parieur expérimenté, ce guide vous donnera toutes les clés pour prendre une décision éclairée.',
              },
              {
                title: 'Meilleures Cotes du Weekend',
                description:
                  'Notre sélection des meilleures cotes pour les matchs de ce weekend.',
                date: '10 Déc 2025',
                tag: 'Analyse',
                color: 'from-purple-500 to-pink-500',
                fullContent:
                  'Chaque weekend, notre équipe d\'experts analyse les cotes de tous les bookmakers français pour vous proposer les meilleures opportunités. Cette semaine, nous avons identifié plusieurs matchs avec des cotes particulièrement intéressantes : le match de Ligue 1 entre le PSG et Marseille, le classico espagnol, et plusieurs rencontres de Premier League. Nous comparons les cotes de tous nos partenaires pour vous garantir les meilleurs rendements possibles. Consultez régulièrement cette section pour ne manquer aucune opportunité de maximiser vos gains !',
              },
            ].map((news, index) => (
              <div
                key={index}
                onClick={() => setSelectedNews(news)}
                className="bg-dark-card border border-dark-border rounded-lg overflow-hidden hover:border-accent-primary transition-all group cursor-pointer"
              >
                <div className={`h-2 bg-gradient-to-r ${news.color}`} />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-accent-primary uppercase">
                      {news.tag}
                    </span>
                    <span className="text-xs text-dark-muted">{news.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-accent-primary transition-colors">
                    {news.title}
                  </h3>
                  <p className="text-dark-muted mb-4">{news.description}</p>
                  <div className="text-accent-primary hover:text-accent-secondary font-semibold text-sm inline-flex items-center">
                    Lire la suite <FiArrowRight className="ml-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News Modal Popup */}
      {selectedNews && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
          onClick={() => setSelectedNews(null)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`h-2 bg-gradient-to-r ${selectedNews.color}`} />
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <span className="text-xs font-semibold text-accent-primary uppercase px-3 py-1 bg-accent-primary/10 rounded-full">
                    {selectedNews.tag}
                  </span>
                  <span className="text-sm text-dark-muted">{selectedNews.date}</span>
                </div>
                <button
                  onClick={() => setSelectedNews(null)}
                  className="text-dark-muted hover:text-white transition-colors p-2 hover:bg-dark-surface rounded-lg"
                  aria-label="Fermer"
                >
                  <FiX size={24} />
                </button>
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">{selectedNews.title}</h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-dark-muted leading-relaxed mb-4 text-lg">
                  {selectedNews.description}
                </p>
                <p className="text-dark-muted leading-relaxed">
                  {selectedNews.fullContent}
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-dark-border flex gap-4">
                <Link
                  to="/comparaison"
                  className="flex-1 bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-3 px-6 rounded-lg text-center transition-colors inline-flex items-center justify-center"
                  onClick={() => setSelectedNews(null)}
                >
                  Comparer les offres <FiArrowRight className="ml-2" />
                </Link>
                <button
                  onClick={() => setSelectedNews(null)}
                  className="px-6 py-3 bg-dark-surface hover:bg-dark-border text-white font-semibold rounded-lg transition-colors border border-dark-border"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent-primary via-accent-secondary to-accent-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Prêt à Commencer ?
          </h2>
          <p className="text-white/90 mb-8 text-lg max-w-2xl mx-auto">
            Rejoignez des milliers de parieurs qui font confiance à Let's Try Bet pour trouver
            les meilleures offres et maximiser leurs gains.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/comparaison"
              className="bg-white text-accent-primary font-semibold py-4 px-8 rounded-lg hover:bg-white/90 transition-colors inline-flex items-center justify-center"
            >
              Comparer les offres <FiArrowRight className="ml-2" />
            </Link>
            <a
              href="#offres"
              className="bg-transparent border-2 border-white text-white font-semibold py-4 px-8 rounded-lg hover:bg-white/10 transition-colors inline-flex items-center justify-center"
            >
              Voir toutes les offres
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section id="faq" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Questions Fréquentes
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: 'Les paris sportifs sont-ils légaux en France ?',
                answer:
                  'Oui, les paris sportifs sont légaux en France depuis 2010. Tous les bookmakers doivent être licenciés par l\'ANJ (Autorité Nationale des Jeux).',
              },
              {
                question: 'Comment retirer mes gains ?',
                answer:
                  'Les retraits sont généralement traités sous 24-48h selon la méthode choisie. Les cartes bancaires et virements sont les plus rapides.',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-lg p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-2">{item.question}</h3>
                <p className="text-dark-muted">{item.answer}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/faq"
              className="text-accent-primary hover:text-accent-secondary font-semibold"
            >
              Voir toutes les questions →
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;

